import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select, 
  message, 
  Spin,
  Switch,
  Space,
  Modal,
  Tag,
  Row,
  Col,
  Divider
} from 'antd';
import { 
  SaveOutlined, 
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  TagOutlined,
  AppstoreOutlined,
  CloseOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate, useParams } from 'react-router-dom';
import { savePost, deletePost, getPostById, categories, tags as predefinedTags } from '../../api/mockData';
import './index.css';

const { TextArea } = Input;
const { Option } = Select;
const { confirm } = Modal;

const WritePost = ({ user }) => {
  const [form] = Form.useForm();
  const [markdownContent, setMarkdownContent] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customTagInput, setCustomTagInput] = useState('');
  const [customTagVisible, setCustomTagVisible] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [allowComments, setAllowComments] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const customTagInputRef = useRef(null);

  useEffect(() => {
    if (isEditMode) {
      fetchPost();
    }
  }, [id]);

  useEffect(() => {
    if (customTagVisible && customTagInputRef.current) {
      customTagInputRef.current.focus();
    }
  }, [customTagVisible]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const data = await getPostById(id);
      if (data.post.authorId !== user.id) {
        message.error('无权编辑此文章');
        navigate('/');
        return;
      }
      
      form.setFieldsValue({
        title: data.post.title,
        content: data.post.content,
        category: data.post.category
      });
      setMarkdownContent(data.post.content);
      setSelectedTags(data.post.tags || []);
      setSelectedCategory(data.post.category || '');
      setIsPublic(data.post.isPublic !== false);
      setAllowComments(data.post.allowComments !== false);
    } catch (error) {
      message.error('获取文章失败');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleTagSelect = (tag) => {
    if (!selectedTags.includes(tag) && selectedTags.length < 10) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const handleCustomTagAdd = () => {
    if (customTagInput.trim()) {
      const newTag = customTagInput.trim();
      if (selectedTags.includes(newTag)) {
        message.warning('标签已存在');
        return;
      }
      if (selectedTags.length >= 10) {
        message.warning('最多只能添加10个标签');
        return;
      }
      if (newTag.length > 20) {
        message.warning('标签长度不能超过20个字符');
        return;
      }
      
      setSelectedTags([...selectedTags, newTag]);
      setCustomTagInput('');
      setCustomTagVisible(false);
      message.success(`已添加标签: ${newTag}`);
    }
  };

  const handleCustomTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomTagAdd();
    }
  };

  const showCustomTagInput = () => {
    setCustomTagVisible(true);
  };

  const hideCustomTagInput = () => {
    setCustomTagVisible(false);
    setCustomTagInput('');
  };

  const handleContentChange = (e) => {
    setMarkdownContent(e.target.value);
  };

  const validateForm = () => {
    if (!form.getFieldValue('title')?.trim()) {
      message.warning('请输入文章标题');
      return false;
    }
    if (!selectedCategory) {
      message.warning('请选择文章分类');
      return false;
    }
    if (selectedTags.length === 0) {
      message.warning('请至少选择一个标签');
      return false;
    }
    if (!markdownContent.trim()) {
      message.warning('请输入文章内容');
      return false;
    }
    return true;
  };

  const handleSave = async (values) => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const postData = {
        ...values,
        tags: selectedTags,
        category: selectedCategory,
        content: markdownContent,
        isPublic,
        allowComments,
        author: user.username,
        authorId: user.id,
        excerpt: markdownContent.substring(0, 100) + '...',
        ...(isEditMode && { id })
      };

      const result = await savePost(postData, user.id);
      message.success(result.message);
      
      // 清除草稿
      localStorage.removeItem('postDraft');
      
      navigate(isEditMode ? `/post/${id}` : `/post/${result.id}`);
    } catch (error) {
      message.error(error.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: '确认删除',
      content: '确定要删除这篇文章吗？删除后无法恢复。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deletePost(id, user.id);
          message.success('文章删除成功');
          navigate('/');
        } catch (error) {
          message.error(error.message || '删除失败');
        }
      }
    });
  };

  const handleSaveDraft = () => {
    const values = form.getFieldsValue();
    const draft = {
      ...values,
      tags: selectedTags,
      category: selectedCategory,
      content: markdownContent,
      isPublic,
      allowComments
    };
    
    localStorage.setItem('postDraft', JSON.stringify(draft));
    message.success('草稿已保存到本地');
  };

  const handleLoadDraft = () => {
    const draft = localStorage.getItem('postDraft');
    if (draft) {
      const draftData = JSON.parse(draft);
      form.setFieldsValue({
        title: draftData.title,
        content: draftData.content,
        category: draftData.category
      });
      setMarkdownContent(draftData.content);
      setSelectedTags(draftData.tags || []);
      setSelectedCategory(draftData.category || '');
      setIsPublic(draftData.isPublic !== false);
      setAllowComments(draftData.allowComments !== false);
      message.success('草稿已加载');
    } else {
      message.info('没有找到草稿');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // 分离预定义标签和自定义标签
  const predefinedTagList = predefinedTags;
  const customTags = selectedTags.filter(tag => !predefinedTagList.includes(tag));

  if (loading) {
    return (
      <div className="write-post-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="write-post-container">
      <Card className="write-post-card">
        <div className="write-post-header">
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleGoBack}
            >
              返回
            </Button>
            <h1 className="write-post-title">
              {isEditMode ? '编辑文章' : '写文章'}
            </h1>
          </Space>
          
          <Space>
            <Switch
              checkedChildren="预览"
              unCheckedChildren="编辑"
              checked={previewMode}
              onChange={setPreviewMode}
            />
            
            <Button
              icon={<SaveOutlined />}
              onClick={handleSaveDraft}
              disabled={!markdownContent.trim()}
            >
              保存草稿
            </Button>
            
            <Button
              onClick={handleLoadDraft}
              disabled={isEditMode}
            >
              加载草稿
            </Button>

            {isEditMode && (
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={handleDelete}
              >
                删除文章
              </Button>
            )}
          </Space>
        </div>

        <Form
          form={form}
          onFinish={handleSave}
          layout="vertical"
          className="write-post-form"
        >
          <Form.Item
            name="title"
            label="文章标题"
            rules={[
              { required: true, message: '请输入文章标题' },
              { max: 100, message: '标题不能超过100个字符' }
            ]}
          >
            <Input
              placeholder="请输入文章标题"
              size="large"
              className="title-input"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    <AppstoreOutlined /> 文章分类
                  </span>
                }
                required
              >
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  placeholder="选择文章分类"
                  size="large"
                  className="category-select"
                >
                  {categories.map(category => (
                    <Option key={category} value={category}>
                      {category}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    <TagOutlined /> 发布设置
                  </span>
                }
              >
                <Space>
                  <span>公开:</span>
                  <Switch 
                    checked={isPublic} 
                    onChange={setIsPublic}
                    checkedChildren="是" 
                    unCheckedChildren="否"
                  />
                  <span>允许评论:</span>
                  <Switch 
                    checked={allowComments} 
                    onChange={setAllowComments}
                    checkedChildren="是" 
                    unCheckedChildren="否"
                  />
                </Space>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={
              <span>
                <TagOutlined /> 文章标签
                <span className="tag-hint">（最多选择10个标签）</span>
              </span>
            }
            required
          >
            <div className="tags-section">
              <div className="selected-tags-container">
                <h4>已选标签 ({selectedTags.length}/10):</h4>
                <div className="selected-tags">
                  {selectedTags.map(tag => (
                    <Tag 
                      key={tag} 
                      closable 
                      onClose={() => handleTagRemove(tag)}
                      className={`selected-tag ${
                        customTags.includes(tag) ? 'custom-tag' : ''
                      }`}
                    >
                      {tag}
                      {customTags.includes(tag) && (
                        <span className="custom-badge">自定义</span>
                      )}
                    </Tag>
                  ))}
                  {selectedTags.length === 0 && (
                    <span className="no-tags-hint">暂无标签，请从下方选择或自定义</span>
                  )}
                </div>
              </div>
              
              <div className="custom-tag-input-container">
                {customTagVisible ? (
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      ref={customTagInputRef}
                      value={customTagInput}
                      onChange={(e) => setCustomTagInput(e.target.value)}
                      onKeyPress={handleCustomTagKeyPress}
                      placeholder="输入自定义标签，按回车确认"
                      maxLength={20}
                      showCount
                    />
                    <Button onClick={handleCustomTagAdd} type="primary">
                      <PlusOutlined />
                    </Button>
                    <Button onClick={hideCustomTagInput}>
                      <CloseOutlined />
                    </Button>
                  </Space.Compact>
                ) : (
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />} 
                    onClick={showCustomTagInput}
                    className="add-custom-tag-btn"
                  >
                    添加自定义标签
                  </Button>
                )}
              </div>
              
              <Divider orientation="left" plain>推荐标签</Divider>
              
              <div className="tag-options-section">
                <h4>预定义标签:</h4>
                <div className="tag-options">
                  {predefinedTagList.map(tag => (
                    <Tag
                      key={tag}
                      className={`tag-option ${selectedTags.includes(tag) ? 'selected' : ''}`}
                      onClick={() => handleTagSelect(tag)}
                    >
                      {tag}
                      {selectedTags.includes(tag) && <PlusOutlined style={{ marginLeft: 4 }} />}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          </Form.Item>

          {previewMode ? (
            <div className="preview-container">
              <h2 className="preview-title">{form.getFieldValue('title') || '无标题'}</h2>
              <div className="preview-meta">
                <Tag color="blue">{selectedCategory || '未分类'}</Tag>
                <div className="preview-tags">
                  {selectedTags.map(tag => (
                    <Tag key={tag} color={customTags.includes(tag) ? 'orange' : 'blue'}>
                      {tag}
                      {customTags.includes(tag) && '*'}
                    </Tag>
                  ))}
                </div>
                <span className="preview-info">作者: {user.username}</span>
              </div>
              <div className="preview-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {markdownContent || '*文章内容为空*'}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <Form.Item
              name="content"
              label="文章内容 (Markdown)"
              rules={[
                { required: true, message: '请输入文章内容' }
              ]}
            >
              <TextArea
                value={markdownContent}
                onChange={handleContentChange}
                placeholder="开始写作吧... (支持Markdown语法)"
                rows={20}
                className="content-textarea"
                showCount
                maxLength={50000}
              />
            </Form.Item>
          )}

          <div className="markdown-help">
            <h4>Markdown 语法提示：</h4>
            <ul>
              <li><code># 标题</code> - 一级标题</li>
              <li><code>**粗体**</code> - 粗体文本</li>
              <li><code>*斜体*</code> - 斜体文本</li>
              <li><code>`代码`</code> - 行内代码</li>
              <li><code>- 列表项</code> - 无序列表</li>
              <li><code>[链接](http://...)</code> - 超链接</li>
              <li><code>![图片](http://...)</code> - 插入图片</li>
            </ul>
          </div>

          <Form.Item className="form-actions">
            <Space>
              <Button
                onClick={handleGoBack}
                size="large"
              >
                取消
              </Button>
              
              <Button
                type="primary"
                icon={isEditMode ? <SaveOutlined /> : <EyeOutlined />}
                htmlType="submit"
                loading={saving}
                size="large"
              >
                {isEditMode ? '更新文章' : '发布文章'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default WritePost;